import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, Instagram, Twitter, Linkedin, Youtube, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useRegional } from '../../context/RegionalContext';

const LOGO_URL = "/amidex-logo-light.svg";

// Office addresses by region - US and UK only
const OFFICE_ADDRESSES = {
  US: {
    name: 'Amidex USA Office',
    address: '311 11th Ave',
    city: 'New York City, 10001',
    country: 'USA'
  },
  GB: {
    name: 'Amidex UK Office',
    address: '12-22 Newhall Street',
    city: 'Birmingham, B3 3AS',
    country: 'United Kingdom'
  }
};

const Footer = () => {
  const { region } = useRegional();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the office address for current region
  const currentOffice = OFFICE_ADDRESSES[region?.code] || OFFICE_ADDRESSES.US;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setIsSubmitting(true);
    setTimeout(() => {
      toast.success('Thank you for subscribing!');
      setEmail('');
      setIsSubmitting(false);
    }, 500);
  };

  const shopLinks = [
    { label: 'All Products', href: '/products' },
    { label: 'My Account', href: '/account' },
    { label: 'Order History', href: '/orders' },
  ];

  const companyLinks = [
    { label: 'Home', href: '/' },
    { label: 'About Us', href: '/about' },
    { label: 'Research', href: '/research' },
    { label: 'Contact', href: '/contact' },
  ];

  const researchToolsLinks = [
    { label: 'Technical Reference Library', href: '/research' },
    { label: 'Institutional Portal', href: '/portal' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Shipping Policy', href: '/shipping' },
    { label: 'Refunds & Returns', href: '/refunds' },
    { label: 'Accessibility', href: '/accessibility' },
  ];

  const socialLinks = [
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Youtube, href: '#', label: 'YouTube' },
  ];

  return (
    <footer className="relative bg-slate-900 text-slate-300 z-10" data-testid="footer">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
          
          {/* Column 1: Brand & B2B Info */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img 
                src={LOGO_URL} 
                alt="Amidex" 
                width="120"
                height="48"
                className="h-12 w-auto brightness-0 invert opacity-90"
              />
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed mb-3">
              B2B analytical-grade laboratory reagents for verified institutional buyers.
            </p>
            <div className="text-xs text-slate-500 space-y-1">
              <p>Institutional credentialing required</p>
              <p>For laboratory use only — not intended for consumer or therapeutic use</p>
            </div>
          </div>

          {/* Column 2: Shop */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">
              Shop
            </h3>
            <ul className="space-y-2.5">
              {shopLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Technical Reference */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">
              Technical Reference
            </h3>
            <ul className="space-y-2.5">
              {researchToolsLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Legal */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">
              Legal
            </h3>
            <ul className="space-y-2.5">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Office Address */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">
              Contact
            </h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-400">
                  <p className="font-medium text-slate-300">{currentOffice.name}</p>
                  <p>{currentOffice.address}</p>
                  <p>{currentOffice.city}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-6 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <div className="text-center sm:text-left">
              <p className="text-sm text-slate-400">
                © {new Date().getFullYear()} Big Lab LLC / Amidex. All rights reserved.
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Amidex is a commercial distributor of laboratory equipment, liquid handling consumables, and technical-grade reagents. Products are intended strictly for non-clinical laboratory analysis and industrial processing.
              </p>
            </div>
            
            {/* Trust Badges */}
            <div className="flex items-center gap-4">
              {/* Show "Made in USA" only for US region */}
              {region?.code === 'US' && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-full border border-slate-700/50">
                  <svg className="w-4 h-4 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                  <span className="text-xs font-medium text-slate-300">Made in USA</span>
                </div>
              )}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-full border border-slate-700/50">
                <svg className="w-4 h-4 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
                <span className="text-xs font-medium text-slate-300">cGMP Certified</span>
              </div>
            </div>
            
            {/* Social Icons */}
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-8 h-8 text-slate-500 hover:text-white hover:bg-slate-800 rounded-full flex items-center justify-center transition-colors"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
